import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Container, Spinner } from 'reactstrap';
import { exportDataPipeline, getDataPipeline, getDataPipelineJob, getDataPipelineRecords } from '../../ReduxToolkit/Reducers/analytics';
import './DataPipeline.css';

// Data Pipeline page, built from hyperscout-data-pipeline.html. Counts, table rows and
// "stopped at" come from /analytics/data-pipeline*; the copy below is UI text only.

const NOUNS = {
  brands: { noun: 'brands', Noun: 'Brands', type: 'brand' },
  retailers: { noun: 'retailers', Noun: 'Retailers', type: 'retailer' },
};

const STAGE_COPY = {
  website: { name: 'Website reachable', q: { brands: 'Can we reach the brand’s own website?', retailers: 'Can we reach the shop’s own website?' } },
  summary: { name: 'AI summary', q: 'Did the crawl produce a usable summary?' },
  fashion: { name: 'Fashion classification', q: 'Is this a fashion business?' },
  entity: { name: 'Entity type', q: { brands: 'Is it actually a brand?', retailers: 'Is it actually a retailer?' } },
  profile: { name: 'Complete profile', q: 'Are all four pillars filled: product category, style, price range, target group?' },
};

const BUCKET_DESC = {
  'website:live': 'Responded with a 2xx status. Ready to crawl.',
  'website:not_checked': 'Has a URL, but the website check has never run on it.',
  'website:no_website': 'URL field is empty.',
  'website:bad_status': 'Redirect, 4xx or 5xx. Site moved, blocked or down.',
  'website:invalid_url': 'The check returned an error instead of a status: invalid link, DNS failure or timeout.',
  'summary:has_summary': 'Summary exists and feeds classification.',
  'summary:no_summary': 'Crawled, but no usable summary: the page is too thin or blocked, or the summary is broken.',
  'fashion:fashion': 'Classified as fashion. Moves to entity check.',
  'fashion:non_fashion': 'Classified outside fashion and excluded from matching.',
  'fashion:unclassified': 'Classifier returned nothing.',
  'entity:monobrand': 'Single-label brand.',
  'entity:brand': 'Classified as a brand.',
  'entity:retailer': 'Multi-brand retailer.',
  'entity:shop': 'Single shop / boutique.',
  'entity:is_brand': 'Not lost: these belong in the brand pool.',
  'entity:other': 'Neither brand nor retailer: agency, marketplace, media, etc.',
  'profile:complete': 'Match-ready.',
  'profile:partial': 'Profiled, but at least one pillar is empty. See breakdown below.',
  'profile:empty': 'Profiling ran but filled nothing.',
  'unaccounted': 'Passed the previous stage but sit in no bucket here. Verify.',
};

// Bulk actions run on the AI side; until those APIs exist they are UI only.
const ACTIONS = {
  'recheck-website': { api: 'recheck_website_status', label: 'Re-check website status', stage: 0, desc: 'Requests each URL again and records the new status code. The previous status is kept as history, never overwritten.' },
  'find-website': { api: 'find_missing_websites', label: 'Find missing websites', stage: 0, desc: 'Searches name + country to propose a domain. Proposals are stored as derived (with confidence and source) and never overwrite a declared URL.' },
  'gen-summary': { api: 'generate_summary', label: 'Generate summary', stage: 1, desc: 'Re-crawls the site and regenerates the AI summary. Records that still produce nothing are flagged as thin sites.' },
  'reclassify-fashion': { api: 'rerun_fashion_classification', label: 'Re-run fashion classification', stage: 2, desc: 'Runs the fashion classifier again on the current summary.' },
  'reclassify-entity': { api: 'rerun_entity_classification', label: 'Re-run entity classification', stage: 3, desc: 'Decides again between brand, monobrand, retailer, shop and other.' },
  'reprofile-missing': { api: 'reprofile_missing_pillars', label: 'Re-profile missing pillars', stage: 4, desc: 'Fills only the empty pillars. Filled pillars and anything a customer declared stay untouched.' },
  'full-reprofile': { api: 'full_reprofile', label: 'Full re-profile (crawl + AI)', stage: 4, desc: 'Re-crawls and regenerates every derived pillar. Declared values still win.' },
  'refresh-working': { api: 'reextract_working_with', label: 'Re-extract working-with', stage: 4, desc: 'Re-reads stockist and brand lists. New links are appended with first_seen; ended links are marked, never deleted.' },
  rematch: { api: 'rerun_matching', label: 'Re-run matching', stage: null, desc: 'Recomputes match scores and reasons for the selected records.' },
};
const BUCKET_ACTION = {
  'website:not_checked': 'recheck-website', 'website:no_website': 'find-website', 'website:bad_status': 'recheck-website',
  'website:invalid_url': 'recheck-website', 'summary:no_summary': 'gen-summary', 'fashion:non_fashion': 'reclassify-fashion',
  'fashion:unclassified': 'reclassify-fashion', 'entity:other': 'reclassify-entity', 'entity:is_brand': 'reclassify-entity',
  'profile:partial': 'reprofile-missing', 'profile:empty': 'full-reprofile',
};

const FILTER_KEYS = ['stage', 'bucket', 'only_missing', 'source', 'q'];
const fmt = (n) => Math.round(n || 0).toLocaleString('en-US');
const pf = (a, b) => {
  if (!b) return '0%';
  const v = (a / b) * 100;
  return (v >= 10 || v === 0 ? v.toFixed(0) : v.toFixed(1)) + '%';
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const passedOf = (stage) => stage.buckets.filter((b) => b.pass).reduce((a, b) => a + b.count, 0);
const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '—');
const AiBadge = () => <span className="tag ai">AI API required</span>;

// Buckets with the forward ones first, each group high to low; a gap becomes "Unaccounted".
const orderedBuckets = (stage) => {
  const buckets = [...stage.buckets].sort((a, b) => b.pass - a.pass || b.count - a.count);
  const gap = stage.in - buckets.reduce((a, b) => a + b.count, 0);
  if (gap > 0) buckets.push({ id: 'unaccounted', label: 'Unaccounted', count: gap, pass: false, warn: true });
  return buckets;
};

/* ===================== Funnel chart ===================== */
const Funnel = ({ data, selected, onSelect }) => {
  const ref = useRef(null);
  const [w, setW] = useState(1200);
  useLayoutEffect(() => {
    const el = ref.current;
    const observer = new ResizeObserver(() => setW(el.clientWidth || 1200));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const H = 300, n = 6, colW = w / n, barW = Math.min(112, colW * 0.48), maxH = 160, cy = 150;
  const vals = [data.total, ...data.stages.map(passedOf)];
  const labels = ['Imported', ...data.stages.map((s) => s.label)];
  const drops = vals.slice(1).map((v, i) => vals[i] - v);
  const maxDrop = drops.indexOf(Math.max(...drops));
  const geo = vals.map((v, i) => {
    const h = Math.max(4, (v / (vals[0] || 1)) * maxH);
    return { x: i * colW + (colW - barW) / 2, h, y: cy - h / 2 };
  });

  return (
    <svg ref={ref} className="funnel-svg" viewBox={`0 0 ${w} ${H}`} role="img" aria-label="Pipeline funnel">
      {geo.slice(0, -1).map((a, i) => {
        const b = geo[i + 1], mx = (a.x + barW + b.x) / 2, c = i === maxDrop ? '#c75a24' : '#5f6672';
        return (
          <g key={`d${i}`}>
            <polygon points={`${a.x + barW},${a.y} ${b.x},${b.y} ${b.x},${b.y + b.h} ${a.x + barW},${a.y + a.h}`} fill={i === maxDrop ? '#f9e6da' : '#ebeef2'} />
            <text x={mx} y={cy - 4} textAnchor="middle" fontWeight="700" fontSize="14" fill={c}>−{fmt(drops[i])}</text>
            <text x={mx} y={cy + 13} textAnchor="middle" fontSize="11.5" fill={c}>{pf(drops[i], vals[i])} dropped</text>
          </g>
        );
      })}
      {geo.map((g, i) => {
        const sel = i === selected + 1;
        const fill = sel ? '#1d1d1b' : i === 0 ? '#9fb6cf' : i === 5 ? '#6a7d2b' : '#3e7cc0';
        return (
          <g key={`c${i}`} className="fcol" onClick={() => onSelect(Math.max(0, i - 1))}>
            <rect x={i * colW} y="0" width={colW} height={H} fill="transparent" />
            <text x={g.x + barW / 2} y="16" textAnchor="middle" fontWeight="700" fontSize="10.5" letterSpacing="1.2" fill="#5f6672">{i === 0 ? 'START' : `STAGE ${i}`}</text>
            <text x={g.x + barW / 2} y="36" textAnchor="middle" fontWeight="600" fontSize="15" fill="#1d1d1b">{labels[i]}</text>
            {sel && <circle cx={g.x + barW / 2} cy="50" r="4" fill="#e2ff5c" stroke="#1d1d1b" strokeWidth="1.5" />}
            <rect className="bar" x={g.x} y={g.y} width={barW} height={g.h} rx="6" fill={fill} />
            <text x={g.x + barW / 2} y={cy + maxH / 2 + 34} textAnchor="middle" fontWeight="700" fontSize="20" fill="#1d1d1b">{fmt(vals[i])}</text>
            <text x={g.x + barW / 2} y={cy + maxH / 2 + 52} textAnchor="middle" fontSize="12" fill="#5f6672">{i === 0 ? '100%' : `${pf(vals[i], vals[0])} of imported`}</text>
          </g>
        );
      })}
    </svg>
  );
};

/* ===================== Page ===================== */
const DataPipeline = () => {
  const dispatch = useDispatch();
  const [params, setParams] = useSearchParams();
  const entity = params.get('entity') === 'retailers' ? 'retailers' : 'brands';
  const N = NOUNS[entity];
  const filter = {
    stage: params.get('stage') || '', bucket: params.get('bucket') || '', only_missing: params.get('only_missing') || '',
    source: params.get('source') || '', q: params.get('q') || '',
  };
  const page = Math.max(1, Number(params.get('page')) || 1);
  const size = [10, 25, 50, 100].includes(Number(params.get('per_page'))) ? Number(params.get('per_page')) : 25;
  const sort = params.get('sort') || '-updated';

  const [summary, setSummary] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);
  const [stageIdx, setStageIdx] = useState(0);
  const [records, setRecords] = useState({ count: 0, data: [] });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [menu, setMenu] = useState(null); // 'sel' | 'act' | { row, top, left }
  const [modal, setModal] = useState(null); // { id, scope }
  const [downstream, setDownstream] = useState(true);
  const [search, setSearch] = useState(filter.q);
  const [exporting, setExporting] = useState(false);
  const tableRef = useRef(null);

  // Every change goes into the URL (new history entry), so the back button works.
  const update = (patch, { resetPage = true } = {}) => {
    const next = new URLSearchParams(params);
    Object.entries({ ...patch, ...(resetPage && { page: '' }) }).forEach(([k, v]) => (v === '' || v == null ? next.delete(k) : next.set(k, String(v))));
    setParams(next);
  };
  const openTable = (patch) => {
    update({ stage: '', bucket: '', only_missing: '', ...patch });
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    let cancelled = false;
    setSummary(null);
    setStageIdx(0);
    dispatch(getDataPipeline(entity)).then((data) => {
      if (cancelled) return;
      setSummary(data || {});
      setLoadedAt(new Date());
    });
    return () => { cancelled = true; };
  }, [dispatch, entity]);

  const filterKey = JSON.stringify([entity, filter]);
  useEffect(() => {
    setSelected(new Set());
    setAllMatching(false);
    setSearch(filter.q);
  }, [filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const query = Object.fromEntries(Object.entries(filter).filter(([, v]) => v));
    dispatch(getDataPipelineRecords(entity, { ...query, page, per_page: size, sort })).then((data) => {
      if (cancelled) return;
      setRecords(data || { count: 0, data: [] });
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [dispatch, filterKey, page, size, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (search === filter.q) return undefined;
    const t = setTimeout(() => update({ q: search.trim() }), 300);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const close = (e) => { if (!e.target.closest('.menu, [data-menu]')) setMenu(null); };
    const esc = (e) => { if (e.key === 'Escape') { setMenu(null); setModal(null); } };
    document.addEventListener('click', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('click', close); document.removeEventListener('keydown', esc); };
  }, []);

  const ready = summary?.stages;
  const stages = ready ? summary.stages : [];
  const stageById = (id) => stages.find((s) => s.id === id);
  const bucketOf = (stageId, bucketId) => stageById(stageId)?.buckets.find((b) => b.id === bucketId);
  const pillarLabel = (id) => summary?.pillars?.items.find((p) => p.id === id)?.label || id;
  const filterLabel = () => {
    if (!filter.stage && !filter.only_missing) return `All ${N.noun}`;
    const parts = [];
    if (filter.stage) parts.push(`Stage ${stages.findIndex((s) => s.id === filter.stage) + 1} › ${bucketOf(filter.stage, filter.bucket)?.label || filter.bucket}`);
    if (filter.only_missing) parts.push(`only ${pillarLabel(filter.only_missing)} missing`);
    return parts.join(' › ');
  };
  const actLabel = (id) => ACTIONS[id]?.label || id;
  const filterQuery = () => Object.fromEntries(Object.entries(filter).filter(([, v]) => v));
  const selCount = allMatching ? records.count : selected.size;

  // Recommended bulk action for what the table shows.
  let recommended = null;
  if (filter.only_missing) recommended = 'reprofile-missing';
  else if (filter.stage && !bucketOf(filter.stage, filter.bucket)?.pass) recommended = BUCKET_ACTION[`${filter.stage}:${filter.bucket}`] || null;

  // scope = which records: { filter } (page filters), { segment } (working-with cell) or { ids }.
  const openAction = (id, given) => {
    setMenu(null);
    setDownstream(true);
    const scope = given || (allMatching
      ? {
        filter: filterQuery(),
        count: records.count,
        label: [filterLabel(), filter.q && `search “${filter.q}”`, filter.source && `source ${filter.source === 'unknown' ? 'Unknown' : filter.source}`].filter(Boolean).join(' · '),
      }
      : { ids: [...selected], count: selected.size, label: 'Hand-picked records' });
    setModal({ id, scope, job: null });
    const request = scope.filter || (scope.segment ? { segment: scope.segment } : { ids: scope.ids });
    dispatch(getDataPipelineJob(entity, request)).then((job) =>
      setModal((m) => (m && m.scope === scope ? { ...m, job: job || { error: true } } : m)));
  };
  const bucketScope = (stageId, bucket) => ({
    filter: { stage: stageId, bucket: bucket.id }, count: bucket.count,
    label: `Stage ${stages.findIndex((s) => s.id === stageId) + 1} › ${bucket.label}`,
  });

  const runExport = async () => {
    setMenu(null);
    setExporting(true);
    await dispatch(exportDataPipeline(entity, allMatching ? filterQuery() : { ids: [...selected] }));
    setExporting(false);
  };

  /* ---------- KPIs ---------- */
  const renderKpis = () => {
    const total = summary.total, complete = passedOf(stages[4]);
    let leak = null;
    stages.forEach((st) => st.buckets.filter((b) => !b.pass).forEach((b) => { if (!leak || b.count > leak.b.count) leak = { st, b }; }));
    const win = [...summary.pillars.items].sort((a, b) => b.only - a.only)[0];
    const leakIdx = stages.indexOf(leak.st);
    return (
      <section className="kpis">
        <div className="kpi dark">
          <div className="eyebrow">Records imported</div>
          <div className="big num">{fmt(total)}</div>
          <div className="k-sub">Every {N.type} in the pool, from all sources.</div>
          <button className="k-link" onClick={() => openTable({})}>Open all in table →</button>
        </div>
        <div className="kpi">
          <div className="eyebrow">Match-ready</div>
          <div className="big num">{fmt(complete)} <span className="tag ok">{pf(complete, total)} of imported</span></div>
          <div className="k-sub">Passed every stage with all four pillars filled: product category, style, price range and target group.</div>
          <button className="k-link" onClick={() => openTable({ stage: 'profile', bucket: 'complete' })}>Open match-ready →</button>
        </div>
        <div className="kpi">
          <div className="eyebrow">Biggest leak</div>
          <div className="big num">{fmt(leak.b.count)} <span className="tag warn">{pf(leak.b.count, total)} of imported</span></div>
          <div className="k-sub">Stage {leakIdx + 1} · {leak.st.label}: <b>{leak.b.label}</b></div>
          <button className="k-link" onClick={() => openAction(BUCKET_ACTION[`${leak.st.id}:${leak.b.id}`], bucketScope(leak.st.id, leak.b))}>
            {actLabel(BUCKET_ACTION[`${leak.st.id}:${leak.b.id}`])} →
          </button>
        </div>
        <div className="kpi">
          <div className="eyebrow">Quickest win</div>
          <div className="big num">+{fmt(win.only)} <span className="tag ok">+{pf(win.only, complete)} match-ready</span></div>
          <div className="k-sub">Profiles where <b>{win.label}</b> is the only missing pillar. Fill that one field and they are complete.</div>
          <button className="k-link" onClick={() => openTable({ stage: 'profile', bucket: 'partial', only_missing: win.id })}>Open these {fmt(win.only)} →</button>
        </div>
      </section>
    );
  };

  /* ---------- Stage detail ---------- */
  const renderBucketRow = (stage, b) => {
    const key = b.id === 'unaccounted' ? 'unaccounted' : `${stage.id}:${b.id}`;
    const w = Math.max(0.4, (b.count / (stage.in || 1)) * 100);
    const action = BUCKET_ACTION[key];
    return (
      <div className="brow" key={b.id}>
        <span className={`ic ${b.pass ? 'ok' : 'bad'}`}>{b.pass ? '✓' : b.warn ? '!' : '✕'}</span>
        <div>
          <div className="lab">{b.label}{b.warn && <span className="tag warn">Check</span>}</div>
          <div className="desc">{BUCKET_DESC[key] || ''}</div>
        </div>
        <div className="track"><div className={`fill ${b.pass ? 'ok' : b.warn ? 'warn' : 'bad'}`} style={{ width: `${w}%` }} /></div>
        <div className="cnt num">{fmt(b.count)}</div>
        <div className="pc num">{pf(b.count, stage.in)}</div>
        <div className="acts">
          {!b.warn && <button className="btn2 sm" onClick={() => openTable({ stage: stage.id, bucket: b.id })}>Open in table</button>}
          {!b.pass && !b.warn && action && (
            <button className="btn2 sm primary" onClick={() => openAction(action, bucketScope(stage.id, b))}>
              {actLabel(action)}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderExtras = () => {
    const P = summary.pillars, W = summary.working, items = [...P.items].sort((a, b) => b.any - a.any);
    const cT = W.cw + W.cwo, pT = W.pw + W.pwo;
    const cell = (cls, value, sub, action, segment, label) => (
      <td className={`c ${cls}`} onClick={() => openAction(action, { segment, count: value, label })}>
        <div className="v num">{fmt(value)}</div><div className="p">{sub}</div>
      </td>
    );
    return (
      <div className="extras">
        <div className="panel2">
          <h3>What is missing in the {fmt(P.base)} partial profiles</h3>
          <div className="ph">A profile can miss several pillars. The dark part is profiles where this is the <b>only</b> gap: fill it and they become match-ready.</div>
          <div className="pleg"><span><i style={{ background: '#f1c3a8' }} />Missing (with others)</span><span><i style={{ background: 'var(--orange)' }} />Only gap</span></div>
          {items.map((it) => (
            <div className="prow" key={it.id}>
              <div style={{ fontWeight: 600 }}>{it.label}</div>
              <div className="ptrack">
                <div className="any" style={{ width: `${(it.any / (P.base || 1)) * 100}%` }} />
                <div className="only" style={{ width: `${Math.max(0.3, (it.only / (P.base || 1)) * 100)}%` }} />
              </div>
              <div className="nums"><b className="num">{fmt(it.any)}</b> missing · {pf(it.any, P.base)}<br /><b className="num" style={{ color: 'var(--orange)' }}>{fmt(it.only)}</b> only gap</div>
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <button className="btn2 sm" onClick={() => openTable({ stage: 'profile', bucket: 'partial', only_missing: it.id })}>Open {fmt(it.only)}</button>
                <button className="btn2 sm primary" onClick={() => openAction('reprofile-missing', { filter: { stage: 'profile', bucket: 'partial', only_missing: it.id }, count: it.only, label: `Stage 5 › only ${it.label} missing` })}>Fill</button>
              </div>
            </div>
          ))}
        </div>
        <div className="panel2">
          <h3>Working-with coverage</h3>
          <div className="ph">Known stockist / brand links. Without them, matching runs on tags only and gives weaker reasons. {fmt(summary.matches)} {N.noun} have at least one match today.</div>
          <table className="mx">
            <tbody>
              <tr><th /><th>With working-with</th><th>Without</th></tr>
              <tr>
                <td className="rh">Complete</td>
                {cell('good', W.cw, `${pf(W.cw, cT)} of complete`, 'refresh-working', 'complete_ww', 'Complete, with working-with')}
                {cell('attn', W.cwo, `${pf(W.cwo, cT)} · re-extract`, 'refresh-working', 'complete_no_ww', 'Complete, without working-with')}
              </tr>
              <tr>
                <td className="rh">Partial</td>
                {cell('', W.pw, `${pf(W.pw, pT)} of partial`, 'reprofile-missing', 'partial_ww', 'Partial, with working-with')}
                {cell('attn', W.pwo, `${pf(W.pwo, pT)} · weakest records`, 'full-reprofile', 'partial_no_ww', 'Partial, without working-with')}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderStage = () => {
    const st = stages[stageIdx], p = passedOf(st), next = stages[stageIdx + 1];
    const buckets = orderedBuckets(st);
    const real = buckets.filter((b) => !b.warn), gap = st.in - real.reduce((a, b) => a + b.count, 0);
    const copy = STAGE_COPY[st.id];
    const question = typeof copy.q === 'string' ? copy.q : copy.q[entity];
    return (
      <section className="card2">
        <div className="sd-head">
          <div><div className="eyebrow">Stage {stageIdx + 1} of 5</div><h2>{copy.name}</h2><p>{question}</p></div>
          <div className="trio">
            <div><div className="l">Came in</div><div className="v num">{fmt(st.in)}</div><div className="p">from {stageIdx ? `Stage ${stageIdx}` : 'import'}</div></div>
            <div><div className="l" style={{ color: 'var(--olive)' }}>Moved forward</div><div className="v num">{fmt(p)}</div><div className="p">{pf(p, st.in)}</div></div>
            <div><div className="l" style={{ color: 'var(--orange)' }}>Dropped here</div><div className="v num">{fmt(st.in - p)}</div><div className="p">{pf(st.in - p, st.in)}</div></div>
          </div>
        </div>
        <div className="group-h">{next ? `Moves forward to Stage ${stageIdx + 2} · ${STAGE_COPY[next.id].name}` : 'Match-ready'}</div>
        {buckets.filter((b) => b.pass).map((b) => renderBucketRow(st, b))}
        <div className="group-h">Drops out here</div>
        {buckets.filter((b) => !b.pass).map((b) => renderBucketRow(st, b))}
        {/* <div className={`recon ${gap ? 'bad' : ''}`}>
          {gap ? '⚠' : '✓'}&nbsp; {fmt(st.in)} in {gap ? '≠' : '='} {real.map((b) => fmt(b.count)).join(' + ')}
          {gap ? ` → ${fmt(gap)} records unaccounted, shown as their own row above` : ''}
        </div> */}
        {stageIdx === 4 && renderExtras()}
      </section>
    );
  };

  /* ---------- Table ---------- */
  const websiteCell = (r) => {
    const s = (r.website_status || '').trim();
    if (/^2\d\d$/.test(s)) return <><span className="dot" />{s}</>;
    if (!s) return r.website ? <span className="tag info">Not checked</span> : <span className="tag warn">None</span>;
    if (/^\d{3}$/.test(s)) return <span className="tag warn">{s}</span>;
    return <span className="tag warn" title={s.slice(0, 200)}>Invalid</span>;
  };
  const rowHTML = (r) => {
    const [stageId, bucketId] = r.stopped_at.split(':');
    const done = r.stopped_at === 'profile:complete';
    const failAt = stages.findIndex((s) => s.id === stageId);
    const isSel = allMatching || selected.has(r.id);
    const missing = stageId !== 'profile' ? <span className="muted">—</span>
      : r.missing?.length ? r.missing.map((m) => <span className="mchip" key={m}>{m}</span>) : <span className="muted">None</span>;
    return (
      <tr key={r.id} className={isSel ? 'sel' : ''}>
        <td><input type="checkbox" className="cb" checked={isSel} onChange={(e) => toggleRow(r.id, e.target.checked)} aria-label={`Select ${r.name}`} /></td>
        <td><div className="nm">{r.name}</div><div className="dm">#{r.id}{r.city ? ` · ${r.city}` : ''} · {r.website || 'no website'}</div></td>
        <td><span className="tag neutral">{r.source || 'Unknown'}</span></td>
        <td>{websiteCell(r)}</td>
        <td>
          <div className={`pipe ${done ? 'done' : ''}`} title={done ? 'Passed all 5 stages' : `Dropped at Stage ${failAt + 1}`}>
            {[0, 1, 2, 3, 4].map((i) => <i key={i} className={done ? '' : i < failAt ? 'd' : i === failAt ? 'f' : ''} />)}
          </div>
        </td>
        <td>{done ? <span className="tag ok">Match-ready</span> : <span className="tag warn">{stages[failAt]?.label} · {bucketOf(stageId, bucketId)?.label}</span>}</td>
        <td>{failAt >= 3 ? capitalize(r.entity) : '—'}</td>
        <td>{missing}</td>
        <td><span className={`qm q${r.quality}`}><i /><i /><i /></span><span className="ql">{['None', 'Poor', 'Mid', 'High'][r.quality]}</span></td>
        <td className="num">{fmtDate(r.updated_at)}</td>
        <td>
          <button className="btn2 ghost sm" data-menu onClick={(e) => { const rect = e.currentTarget.getBoundingClientRect(); setMenu({ row: r, top: rect.bottom + 4, left: rect.right - 280 }); }} aria-label="Row actions">⋯</button>
        </td>
      </tr>
    );
  };
  const toggleRow = (id, on) => {
    const next = new Set(allMatching ? records.data.map((r) => r.id) : selected);
    if (on) next.add(id); else next.delete(id);
    setAllMatching(false);
    setSelected(next);
  };
  const pageIds = records.data.map((r) => r.id);
  const onPage = pageIds.filter((id) => selected.has(id)).length;
  const headerChecked = allMatching || (pageIds.length > 0 && onPage === pageIds.length);
  const selectPage = () => { setAllMatching(false); setSelected(new Set([...selected, ...pageIds])); setMenu(null); };
  const clearSel = () => { setSelected(new Set()); setAllMatching(false); setMenu(null); };
  const sortBy = (key) => {
    const [cur, dir] = sort.startsWith('-') ? [sort.slice(1), 'desc'] : [sort, 'asc'];
    const nextDir = cur === key ? (dir === 'asc' ? 'desc' : 'asc') : key === 'name' ? 'asc' : 'desc';
    update({ sort: nextDir === 'desc' ? `-${key}` : key });
  };
  const sortMark = (key) => (sort === key ? ' ↑' : sort === `-${key}` ? ' ↓' : '');
  const pages = Math.max(1, Math.ceil(records.count / size));
  // const urlLine = `/data-pipeline?${new URLSearchParams(Object.entries({ entity, ...filter, page, per_page: size, sort }).filter(([, v]) => v)).toString()}`;

  const renderTable = () => (
    <section className="card2" ref={tableRef}>
      <div className="card-h" style={{ marginBottom: 6 }}>
        <div>
          <h2>{N.Noun}</h2>
          <div className="countline"><b className="num">{fmt(records.count)}</b> {N.noun} · {ready ? filterLabel() : ''}{filter.source ? ` · source ${filter.source === 'unknown' ? 'Unknown' : filter.source}` : ''}</div>
        </div>
        {/* <span className="url">{urlLine}</span> */}
      </div>
      <div className="tbar">
        <input className="input2" style={{ width: 280 }} placeholder="Search name, domain or ID" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="input2" style={{ minWidth: 330 }} value={filter.stage ? `${filter.stage}:${filter.bucket}` : ''}
          onChange={(e) => { const [s, b] = e.target.value.split(':'); update({ stage: s || '', bucket: b || '', only_missing: '' }); }}>
          <option value="">Pipeline outcome: all {N.noun}</option>
          {stages.map((st, si) => (
            <optgroup key={st.id} label={`Stage ${si + 1} · ${STAGE_COPY[st.id].name}`}>
              {orderedBuckets(st).filter((b) => !b.warn).map((b) => <option key={b.id} value={`${st.id}:${b.id}`}>{b.pass ? '✓' : '✕'} {b.label} ({fmt(b.count)})</option>)}
            </optgroup>
          ))}
        </select>
        <select className="input2" value={filter.source} onChange={(e) => update({ source: e.target.value })}>
          <option value="">All sources</option>
          {(summary?.sources || []).map((s) => <option key={s.name} value={s.name}>{s.name === 'unknown' ? 'Unknown' : s.name} ({fmt(s.count)})</option>)}
        </select>
        <button className="btn2 ghost sm" onClick={() => { setSearch(''); update({ stage: '', bucket: '', only_missing: '', source: '', q: '' }); }}>Clear filters</button>
      </div>
      <div className="chips">
        {filter.stage && <span className="fchip">Stage {stages.findIndex((s) => s.id === filter.stage) + 1} › {bucketOf(filter.stage, filter.bucket)?.label || filter.bucket}<button onClick={() => update({ stage: '', bucket: '', only_missing: '' })} aria-label="Remove stage filter">✕</button></span>}
        {filter.only_missing && <span className="fchip">Only missing: {pillarLabel(filter.only_missing)}<button onClick={() => update({ only_missing: '' })} aria-label="Remove pillar filter">✕</button></span>}
        {filter.source && <span className="fchip">Source: {filter.source === 'unknown' ? 'Unknown' : filter.source}<button onClick={() => update({ source: '' })} aria-label="Remove source filter">✕</button></span>}
        {filter.q && <span className="fchip">“{filter.q}”<button onClick={() => { setSearch(''); update({ q: '' }); }} aria-label="Remove search">✕</button></span>}
        {!FILTER_KEYS.some((k) => filter[k]) && <span className="muted" style={{ fontSize: 12.5 }}>No filters. Pick a stage outcome above, or click any number on this page.</span>}
      </div>
      {allMatching ? (
        <div className="selbanner">All <b>{fmt(records.count)}</b> {N.noun} matching <b>{filterLabel()}</b> are selected, across {fmt(pages)} pages.<button onClick={clearSel}>Clear selection</button></div>
      ) : pageIds.length > 0 && onPage === pageIds.length && records.count > pageIds.length ? (
        <div className="selbanner">All <b>{pageIds.length}</b> {N.noun} on this page are selected.<button onClick={() => setAllMatching(true)}>Select all {fmt(records.count)} {N.noun} matching this filter</button></div>
      ) : null}
      <div className="tablewrap">
        <table className="dt">
          <thead>
            <tr>
              <th style={{ width: 52 }}>
                <span className="hcb">
                  <input type="checkbox" className="cb" aria-label="Select page" checked={headerChecked}
                    ref={(el) => { if (el) el.indeterminate = !headerChecked && onPage > 0; }}
                    onChange={(e) => (e.target.checked ? selectPage() : clearSel())} />
                  <button className="caret" data-menu aria-label="Selection options" onClick={() => setMenu(menu === 'sel' ? null : 'sel')}>▼</button>
                  {menu === 'sel' && (
                    <div className="menu" style={{ top: 30, left: -6 }}>
                      <div className="mh">Select</div>
                      <button onClick={selectPage}>This page <span className="sm">{pageIds.length} rows</span></button>
                      <button onClick={() => { setAllMatching(true); setMenu(null); }}>All pages matching filter <span className="sm">{fmt(records.count)} rows</span></button>
                      <hr />
                      <button onClick={clearSel}>Clear selection</button>
                    </div>
                  )}
                </span>
              </th>
              <th className="sortable" onClick={() => sortBy('name')}>Name{sortMark('name')}</th>
              <th>Source</th>
              <th>Website</th>
              <th>Pipeline</th>
              <th>Stopped at</th>
              <th>Type</th>
              <th>Missing pillars</th>
              <th className="sortable" onClick={() => sortBy('quality')}>Data quality{sortMark('quality')}</th>
              <th className="sortable" onClick={() => sortBy('updated')}>Updated{sortMark('updated')}</th>
              <th style={{ width: 44 }} />
            </tr>
          </thead>
          <tbody className={loading ? 'loading-row' : ''}>
            {records.data.length ? records.data.map(rowHTML)
              : <tr><td colSpan="11" className="empty">{loading ? <Spinner size="sm" /> : `No ${N.noun} match these filters.`}</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="pager">
        <div>Rows per page <select className="input2" style={{ height: 32, marginLeft: 6 }} value={size} onChange={(e) => update({ per_page: e.target.value })}>
          {[10, 25, 50, 100].map((n) => <option key={n}>{n}</option>)}
        </select></div>
        <div className="num">{fmt(records.count ? (page - 1) * size + 1 : 0)}–{fmt(Math.min(records.count, page * size))} of {fmt(records.count)}</div>
        <div className="pg">
          <button className="pgbtn" disabled={page <= 1} onClick={() => update({ page: 1 }, { resetPage: false })} aria-label="First page">«</button>
          <button className="pgbtn" disabled={page <= 1} onClick={() => update({ page: page - 1 }, { resetPage: false })} aria-label="Previous page">‹</button>
          <span className="num" style={{ padding: '0 8px' }}>Page {fmt(page)} of {fmt(pages)}</span>
          <button className="pgbtn" disabled={page >= pages} onClick={() => update({ page: page + 1 }, { resetPage: false })} aria-label="Next page">›</button>
          <button className="pgbtn" disabled={page >= pages} onClick={() => update({ page: pages }, { resetPage: false })} aria-label="Last page">»</button>
        </div>
      </div>
    </section>
  );

  /* ---------- Bulk bar, row menu, modal ---------- */
  const renderBulk = () => {
    const touched = allMatching ? 'All matching · ' + filterLabel() : 'Picked by hand';
    return (
      <div className="bulk">
        <div><div className="bc num">{fmt(selCount)} selected</div><div className="bs">{touched}</div></div>
        <div className="sep" />
        {recommended && <button className="btn2 neon" onClick={() => openAction(recommended)}>★ {actLabel(recommended)}</button>}
        <div style={{ position: 'relative' }}>
          <button className="btn2" data-menu onClick={() => setMenu(menu === 'act' ? null : 'act')}>All actions ▲</button>
          {menu === 'act' && (
            <div className="menu act-menu">
              {stages.map((st, si) => {
                const ids = Object.keys(ACTIONS).filter((id) => ACTIONS[id].stage === si);
                return ids.length ? (
                  <div key={st.id}>
                    <div className="mh">Stage {si + 1} · {st.label}</div>
                    {ids.map((id) => <button key={id} onClick={() => openAction(id)}>{actLabel(id)} <span>{id === recommended && <span className="tag ok">Recommended</span>} <AiBadge /></span></button>)}
                  </div>
                ) : null;
              })}
              <div className="mh">Matching &amp; export</div>
              <button onClick={() => openAction('rematch')}>{actLabel('rematch')} <AiBadge /></button>
              <button onClick={runExport}>Export CSV <span className="sm">{fmt(selCount)} rows</span></button>
            </div>
          )}
        </div>
        <button className="btn2" onClick={runExport} disabled={exporting}>{exporting ? 'Exporting…' : 'Export CSV'}</button>
        <button className="btn2" onClick={clearSel} title="Clear selection" aria-label="Clear selection">✕</button>
      </div>
    );
  };

  const renderRowMenu = () => {
    const r = menu.row;
    const action = r.stopped_at === 'profile:complete' ? 'rematch' : BUCKET_ACTION[r.stopped_at];
    return (
      <div className="menu" style={{ position: 'fixed', top: menu.top, left: Math.max(8, menu.left) }}>
        <button onClick={() => { setMenu(null); window.open(`/profile/${r.id}/${N.type}`, '_blank'); }}>View profile</button>
        <hr />
        {action && <button onClick={() => openAction(action, { ids: [r.id], count: 1, label: `${r.name} (#${r.id})` })}>{actLabel(action)} <AiBadge /></button>}
      </div>
    );
  };

  const renderModal = () => {
    const A = ACTIONS[modal.id], scope = modal.scope, job = modal.job;
    const continues = A.stage != null && A.stage < 4;
    // Shortened for display only: the real request carries every id.
    const shownIds = (ids) => (ids.length > 5 ? [...ids.slice(0, 5), `… ${fmt(ids.length - 5)} more`] : ids);
    const payload = job && !job.error && {
      action: A.api,
      entity: N.type,
      description: scope.label,
      count: job.count,
      records: job.records.query ? { query: job.records.query } : { ids: shownIds(job.records.ids) },
      ...(continues && { continue_downstream: downstream }),
    };
    return (
      <div className="scrim" onClick={(e) => e.target === e.currentTarget && setModal(null)}>
        <div className="modal2" role="dialog" aria-modal="true" aria-label={A.label}>
          <div className="eyebrow">Bulk action · runs as a job</div>
          <h3>{A.label}</h3>
          <div className="scope">
            <div><div className="v num">{fmt(scope.count)} {N.noun}</div><div className="muted" style={{ fontSize: 13 }}>{scope.label}</div></div>
            <AiBadge />
          </div>
          <p className="d">{A.desc}</p>
          {continues && (
            <label className="opt"><input type="checkbox" className="cb" checked={downstream} onChange={(e) => setDownstream(e.target.checked)} />
              <span><b>Continue downstream.</b> Records that pass this step flow on through Stage {A.stage + 2}–5 automatically.</span></label>
          )}
          <div className="eyebrow" style={{ marginTop: 8 }}>Request to send to the AI API</div>
          <pre className="code">{!job ? 'Preparing…' : job.error ? 'Could not prepare the request.' : `POST /api/jobs\n${JSON.stringify(payload, null, 2)}`}</pre>
          <div className="ai-note">This action runs on the AI side. The AI API isn't connected yet, so the job can't be queued from here.</div>
          <div className="mfoot">
            <button className="btn2" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn2 primary" disabled title="AI API required">Queue job</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="page-body">
      <Container fluid className="dp">
        <section className="pagehead">
          <div>
            <div className="eyebrow">Admin · Data quality</div>
            <h1>How raw records become match-ready</h1>
            <p className="sub">Read left to right. Each stage only counts what passed the stage before, and every stage adds up to exactly what came in. Click any number to open those records.</p>
          </div>
          <div>
            <div className="seg">
              {['brands', 'retailers'].map((e) => (
                <button key={e} className={entity === e ? 'on' : ''}
                  onClick={() => { if (e !== entity) { setSearch(''); setParams(new URLSearchParams(e === 'retailers' ? { entity: e } : {})); } }}>
                  {NOUNS[e].Noun}
                </button>
              ))}
            </div>
            <div className="meta">Live counts{loadedAt ? ` · loaded ${loadedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : ''}</div>
          </div>
        </section>

        {!summary ? (
          <div className="d-flex justify-content-center py-5"><Spinner color="dark" /></div>
        ) : !ready ? (
          <div className="card2 text-center muted">Couldn't load the data pipeline.</div>
        ) : (
          <>
            {renderKpis()}
            <section className="card2">
              <div className="card-h">
                <div><h2>Funnel</h2><p>Records remaining after each stage. The number between two bars is what dropped out there.</p></div>
                <div className="legend">
                  <span><i style={{ background: 'var(--blue-2)' }} />Imported</span>
                  <span><i style={{ background: 'var(--blue)' }} />Passed stage</span>
                  <span><i style={{ background: 'var(--ink)' }} />Selected</span>
                  <span><i style={{ background: 'var(--olive)' }} />Match-ready</span>
                  <span><i style={{ background: 'var(--orange)' }} />Biggest drop</span>
                </div>
              </div>
              <Funnel data={summary} selected={stageIdx} onSelect={setStageIdx} />
              <div className="stagetabs">
                <div className="hint">Pick a stage to see where its records went ↓</div>
                {stages.map((st, i) => (
                  <button key={st.id} className={`stab ${i === stageIdx ? 'on' : ''}`} onClick={() => setStageIdx(i)}>
                    <span className="n">Stage {i + 1}</span><span className="t">{STAGE_COPY[st.id].name}</span>
                    <span className="r"><span>{fmt(passedOf(st))} passed</span><span>{pf(passedOf(st), st.in)}</span></span>
                  </button>
                ))}
              </div>
            </section>
            {renderStage()}
          </>
        )}
        {renderTable()}
        {selCount > 0 && renderBulk()}
        {menu?.row && renderRowMenu()}
        {modal && renderModal()}
      </Container>
    </div>
  );
};

export default DataPipeline;
